import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Grid2,
    Typography,
    TextField,
    IconButton,
    InputAdornment,
    Tooltip,
    Box,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockIcon from '@mui/icons-material/Lock';
import LanguageIcon from '@mui/icons-material/Language';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import EditorImport from 'react-simple-code-editor';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import 'highlight.js/styles/github.css';

hljs.registerLanguage('javascript', javascript);
// Vite's CJS interop wraps the default twice for this package.
const Editor =
    typeof EditorImport === 'function'
        ? EditorImport
        : typeof EditorImport?.default === 'function'
          ? EditorImport.default
          : null;

class CodeEditorErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(err) {
        console.error('Verification editor crashed, falling back to textarea:', err);
    }
    render() {
        if (this.state.hasError || !Editor) return this.props.fallback;
        return this.props.children;
    }
}

import { useGenerateVerificationScript } from '@openapi/shows/shows';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';
import { useGetTicketOptions } from '@openapi/ticket-options/ticket-options';
import { MODAL_TYPE } from '../../hooks/useModal';

const DEFAULT_LANG = 'en';

// Mirror of backend/src/services/ScriptGenerationService.ts INPUT_SHAPE_DOC.
// Surfaced verbatim so admins can verify the LLM sees the exact runtime
// input the script will receive.
const INPUT_SHAPE_DOC = `type Input = {
  startTime: string; // ISO 8601
  cart: Array<{
    ticketTypeName: string;
    ticketOptionName: string;
    quantity: number;
    canBuyAlone: boolean;
    blockLimit: number | null;
    automaticSeating: string;
    unitNetPrice: number; // cents
    unitFee: number;      // cents
    unitTax: number;      // cents
    unitGross: number;    // cents
    seats: Array<{ name: string | null }>;
  }>;
  fields: Array<{ label: string; value: string }>;
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    language: string;
  };
  coupon: {
    code: string;
    discountType: 'percentage' | 'fixed';
    discountValue: number;
    discountAmount: number;
  } | null;
  totals: {
    subtotalNet: number;
    totalFees: number;
    totalTax: number;
    grossBeforeDiscount: number;
    discount: number;
    finalBeforeScript: number;
  };
};

// Return integer cents (delta) or:
// throw new Error(901);`;

const SAMPLE_INPUT = `{
  "startTime": "2026-06-01T18:00:00.000Z",
  "cart": [
    {
      "ticketTypeName": "General",
      "ticketOptionName": "Adult",
      "quantity": 2,
      "canBuyAlone": true,
      "blockLimit": null,
      "automaticSeating": "",
      "unitNetPrice": 5000,
      "unitFee": 200,
      "unitTax": 400,
      "unitGross": 5600,
      "seats": [{ "name": null }, { "name": null }]
    }
  ],
  "fields": [],
  "contact": {
    "firstName": "Test",
    "lastName": "User",
    "email": "test@example.com",
    "phone": "+10000000000",
    "language": "en"
  },
  "coupon": null,
  "totals": {
    "subtotalNet": 10000,
    "totalFees": 400,
    "totalTax": 800,
    "grossBeforeDiscount": 11200,
    "discount": 0,
    "finalBeforeScript": 11200
  }
}`;

/**
 * Editor for a show's verification calculation script. Default view is a
 * natural-language prompt + Generate button (LLM-backed). The Advanced
 * accordion exposes the raw script and the per-language error message
 * dictionary. Editing the script directly flips a "locked" flag; subsequent
 * generations warn before overwriting.
 */
const ShowVerificationSection = React.memo(
    ({ values, setFieldValue, showId, showModal, notifyError, notifySuccess }) => {
        const { t } = useTranslation();
        const generateMutation = useGenerateVerificationScript();
        const [advancedOpen, setAdvancedOpen] = useState(false);

        // Pull ticket type / option names so we can show them as comments
        // alongside the input shape — both for the admin reading the help
        // block and for the LLM (the backend re-loads them server-side).
        const { data: ticketTypesData } = useGetTicketTypes(
            { optionalId: Number(showId) },
            { query: { enabled: !!showId && Number.isFinite(Number(showId)) } },
        );
        const { data: ticketOptionsData } = useGetTicketOptions(
            { optionalId: Number(showId) },
            { query: { enabled: !!showId && Number.isFinite(Number(showId)) } },
        );
        const ticketTypeNames = useMemo(
            () =>
                (
                    ticketTypesData?.tickettypes ||
                    ticketTypesData?.ticketTypes ||
                    ticketTypesData ||
                    []
                )
                    .map?.((tt) => tt?.name)
                    ?.filter(Boolean) || [],
            [ticketTypesData],
        );
        const ticketOptionNames = useMemo(
            () =>
                (
                    ticketOptionsData?.ticketoptions ||
                    ticketOptionsData?.ticketOptions ||
                    ticketOptionsData ||
                    []
                )
                    .map?.((to) => to?.name)
                    ?.filter(Boolean) || [],
            [ticketOptionsData],
        );

        const namesComment = useMemo(() => {
            if (ticketTypeNames.length === 0 && ticketOptionNames.length === 0) return '';
            const lines = ['', '// For this show:'];
            if (ticketTypeNames.length > 0) {
                lines.push(
                    `//   ticketTypeName ∈ { ${ticketTypeNames
                        .map((n) => JSON.stringify(n))
                        .join(', ')} }`,
                );
            }
            if (ticketOptionNames.length > 0) {
                lines.push(
                    `//   ticketOptionName ∈ { ${ticketOptionNames
                        .map((n) => JSON.stringify(n))
                        .join(', ')} }`,
                );
            }
            return lines.join('\n');
        }, [ticketTypeNames, ticketOptionNames]);

        const supportedLanguages = useMemo(() => {
            const langs = Array.isArray(values.venueAvailableLanguages)
                ? values.venueAvailableLanguages.filter(Boolean)
                : [];
            return langs.includes(DEFAULT_LANG) ? langs : [DEFAULT_LANG, ...langs];
        }, [values.venueAvailableLanguages]);

        const verificationMessages = values.verificationMessages || {};
        const isLocked = !!values.verificationScriptLocked;
        const hasScript = !!(values.verificationScript || '').trim();
        const promptText = (values.verificationPrompt || '').trim();
        const canGenerate =
            !!showId && Number.isFinite(showId) && !!promptText && !generateMutation.isPending;

        // Rows live in local state so a freshly-added empty row survives
        // (the formik value drops rows without a code+message).
        const deriveRows = useCallback(
            (messages) => {
                const codeSet = new Set();
                for (const lang of Object.keys(messages || {})) {
                    for (const code of Object.keys(messages[lang] || {})) {
                        codeSet.add(code);
                    }
                }
                const codes = Array.from(codeSet).sort((a, b) => {
                    const na = Number(a);
                    const nb = Number(b);
                    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
                    return String(a).localeCompare(String(b));
                });
                return codes.map((code) => {
                    const row = { code };
                    for (const lang of supportedLanguages) {
                        row[lang] = messages?.[lang]?.[code] ?? '';
                    }
                    return row;
                });
            },
            [supportedLanguages],
        );

        const projectRows = useCallback(
            (nextRows) => {
                const out = {};
                for (const row of nextRows) {
                    const code = String(row.code || '').trim();
                    if (!code) continue;
                    for (const lang of supportedLanguages) {
                        const msg = (row[lang] || '').trim();
                        if (!msg) continue;
                        if (!out[lang]) out[lang] = {};
                        out[lang][code] = msg;
                    }
                }
                return out;
            },
            [supportedLanguages],
        );

        const [rows, setRows] = useState(() => deriveRows(verificationMessages));

        // Re-seed from formik when an external write lands (e.g. an LLM
        // generation merges new English messages in). Skip when the formik
        // value matches our own projection — that means we're the source.
        useEffect(() => {
            setRows((prev) => {
                const projected = JSON.stringify(projectRows(prev));
                const incoming = JSON.stringify(verificationMessages || {});
                if (projected === incoming) return prev;
                return deriveRows(verificationMessages);
            });
        }, [verificationMessages, deriveRows, projectRows]);

        const updateRow = useCallback(
            (idx, field, value) => {
                setRows((prev) => {
                    const next = prev.map((row, i) =>
                        i === idx ? { ...row, [field]: value } : row,
                    );
                    setFieldValue('verificationMessages', projectRows(next));
                    return next;
                });
            },
            [projectRows, setFieldValue],
        );

        const addRow = useCallback(() => {
            setRows((prev) => [...prev, { code: '' }]);
        }, []);

        const removeRow = useCallback(
            (idx) => {
                setRows((prev) => {
                    const next = prev.filter((_, i) => i !== idx);
                    setFieldValue('verificationMessages', projectRows(next));
                    return next;
                });
            },
            [projectRows, setFieldValue],
        );

        const openTranslations = useCallback(
            (idx) => {
                const row = rows[idx];
                if (!row) return;
                // Build the per-language map the standard TRANSLATIONS modal
                // expects. We include all languages we already track on the
                // row (the modal itself loads the venue's full language
                // list via getVenue and renders one field per).
                const translations = {};
                for (const key of Object.keys(row)) {
                    if (key === 'code') continue;
                    if (typeof row[key] === 'string' && row[key]) {
                        translations[key] = row[key];
                    }
                }
                showModal(MODAL_TYPE.TRANSLATIONS, {
                    translations,
                    setTranslations: (next) => {
                        setRows((prev) => {
                            const updated = prev.map((r, i) => {
                                if (i !== idx) return r;
                                const merged = { code: r.code };
                                for (const [lang, msg] of Object.entries(next || {})) {
                                    if (typeof msg === 'string' && msg) merged[lang] = msg;
                                }
                                // Preserve the English message so the row's
                                // default-language input keeps its value
                                // even if the user didn't touch it inside
                                // the modal.
                                if (!merged[DEFAULT_LANG] && r[DEFAULT_LANG]) {
                                    merged[DEFAULT_LANG] = r[DEFAULT_LANG];
                                }
                                return merged;
                            });
                            setFieldValue('verificationMessages', projectRows(updated));
                            return updated;
                        });
                    },
                    item: 'shows',
                    intermediateColumn: 'verification',
                    column: 'messageLabel',
                    venueId: values.venueId,
                    originalString: row[DEFAULT_LANG] || '',
                });
            },
            [rows, showModal, values.venueId, projectRows, setFieldValue],
        );

        // Manually editing the script flips the lock; one-way until the next
        // successful generation clears it.
        const onScriptChange = useCallback(
            (value) => {
                setFieldValue('verificationScript', value);
                if (!isLocked) {
                    setFieldValue('verificationScriptLocked', true);
                }
            },
            [setFieldValue, isLocked],
        );

        const runGeneration = useCallback(async () => {
            try {
                const res = await generateMutation.mutateAsync({
                    id: showId,
                    data: { prompt: promptText },
                });
                setFieldValue('verificationScript', res.script);
                setFieldValue('verificationScriptLocked', false);

                // Merge English messages into the existing per-language map.
                const next = {};
                for (const [lang, codes] of Object.entries(verificationMessages)) {
                    next[lang] = { ...codes };
                }
                if (!next.en) next.en = {};
                for (const [code, msg] of Object.entries(res.errorMessages || {})) {
                    next.en[code] = msg;
                }
                setFieldValue('verificationMessages', next);

                setAdvancedOpen(true);
                if (notifySuccess) notifySuccess(t('shows.verification.generationSuccess'));
            } catch (err) {
                if (notifyError) notifyError(err);
            }
        }, [
            generateMutation,
            showId,
            promptText,
            setFieldValue,
            verificationMessages,
            notifySuccess,
            notifyError,
            t,
        ]);

        const [testInput, setTestInput] = useState(SAMPLE_INPUT);
        const [testOutput, setTestOutput] = useState(null);
        const [isFormatting, setIsFormatting] = useState(false);
        const [copyAck, setCopyAck] = useState(false);

        const formatScript = useCallback(async () => {
            if (!hasScript || isFormatting) return;
            setIsFormatting(true);
            try {
                const prettier = await import('prettier/standalone');
                const babel = await import('prettier/plugins/babel');
                const estree = await import('prettier/plugins/estree');
                const formatted = await prettier.format(values.verificationScript || '', {
                    parser: 'babel',
                    plugins: [babel.default || babel, estree.default || estree],
                    semi: true,
                    singleQuote: true,
                    tabWidth: 2,
                    printWidth: 100,
                });
                setFieldValue('verificationScript', formatted);
                if (!isLocked) setFieldValue('verificationScriptLocked', true);
            } catch (err) {
                if (notifyError) notifyError(err);
            } finally {
                setIsFormatting(false);
            }
        }, [
            hasScript,
            isFormatting,
            values.verificationScript,
            setFieldValue,
            isLocked,
            notifyError,
        ]);

        const copyScript = useCallback(async () => {
            try {
                await navigator.clipboard.writeText(values.verificationScript || '');
                setCopyAck(true);
                setTimeout(() => setCopyAck(false), 1500);
            } catch (err) {
                if (notifyError) notifyError(err);
            }
        }, [values.verificationScript, notifyError]);

        const runTest = useCallback(() => {
            const script = values.verificationScript || '';
            if (!script.trim()) {
                setTestOutput({ ok: false, kind: 'empty' });
                return;
            }
            let parsedInput;
            try {
                parsedInput = JSON.parse(testInput);
            } catch (e) {
                setTestOutput({ ok: false, kind: 'badInput', message: String(e.message || e) });
                return;
            }
            const t0 =
                typeof performance !== 'undefined' && performance.now
                    ? performance.now()
                    : Date.now();
            try {
                const fn = new Function('input', script);
                const result = fn(parsedInput);
                const elapsed =
                    (typeof performance !== 'undefined' && performance.now
                        ? performance.now()
                        : Date.now()) - t0;
                setTestOutput({ ok: true, value: result, elapsed });
            } catch (e) {
                const elapsed =
                    (typeof performance !== 'undefined' && performance.now
                        ? performance.now()
                        : Date.now()) - t0;
                const codeMatch = String(e.message || '').trim();
                const numericCode = /^\d+$/.test(codeMatch) ? codeMatch : null;
                const mappedMessage = numericCode
                    ? verificationMessages?.[DEFAULT_LANG]?.[numericCode] || null
                    : null;
                setTestOutput({
                    ok: false,
                    kind: 'thrown',
                    message: String(e.message || e),
                    stack: e.stack ? String(e.stack).split('\n').slice(0, 4).join('\n') : null,
                    code: numericCode,
                    mappedMessage,
                    elapsed,
                });
            }
        }, [values.verificationScript, testInput, verificationMessages]);

        const resetTestInput = useCallback(() => {
            setTestInput(SAMPLE_INPUT);
            setTestOutput(null);
        }, []);

        const onGenerateClick = useCallback(() => {
            if (isLocked && hasScript) {
                showModal(MODAL_TYPE.CONFIRMATION, {
                    title: t('shows.verification.lockedConfirmTitle'),
                    description: t('shows.verification.lockedConfirmBody'),
                    confirm: runGeneration,
                });
                return;
            }
            runGeneration();
        }, [isLocked, hasScript, showModal, runGeneration, t]);

        return (
            <>
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        label={t('shows.verification.promptLabel')}
                        placeholder={t('shows.verification.promptPlaceholder')}
                        value={values.verificationPrompt || ''}
                        onChange={(e) => setFieldValue('verificationPrompt', e.target.value)}
                        multiline
                        minRows={4}
                        maxRows={10}
                        fullWidth
                        sx={{ mt: 1 }}
                    />
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2 }}
                    >
                        <Button
                            startIcon={
                                generateMutation.isPending ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <AutoAwesomeIcon />
                                )
                            }
                            onClick={onGenerateClick}
                            variant="contained"
                            disabled={!canGenerate}
                        >
                            {generateMutation.isPending
                                ? t('shows.verification.generating')
                                : hasScript
                                  ? t('shows.verification.regenerateButton')
                                  : t('shows.verification.generateButton')}
                        </Button>
                        {hasScript && (
                            <Chip
                                size="small"
                                color={isLocked ? 'warning' : 'success'}
                                icon={isLocked ? <LockIcon /> : <AutoAwesomeIcon />}
                                label={
                                    isLocked
                                        ? t('shows.verification.lockedChip')
                                        : t('shows.verification.generatedChip')
                                }
                            />
                        )}
                    </Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1, textAlign: 'left' }}
                    >
                        {t('shows.verification.generationBehaviorHint')}
                    </Typography>
                </Grid2>

                <Grid2 size={{ xs: 12 }} sx={{ mt: 2 }}>
                    <Accordion
                        expanded={advancedOpen}
                        onChange={(_, expanded) => setAdvancedOpen(expanded)}
                        disableGutters
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1">
                                {t('shows.verification.advancedToggle')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid2 container spacing={2} size={{ xs: 12 }}>
                                {promptText && (
                                    <Grid2 size={{ xs: 12 }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block' }}
                                        >
                                            {t('shows.verification.helpPromptLabel')}
                                        </Typography>
                                        <Box
                                            component="pre"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.78rem',
                                                whiteSpace: 'pre-wrap',
                                                textAlign: 'left',
                                                bgcolor: 'grey.100',
                                                borderRadius: 1,
                                                p: 1.5,
                                                mt: 0.5,
                                                mb: 0,
                                            }}
                                        >
                                            {promptText}
                                        </Box>
                                    </Grid2>
                                )}

                                <Grid2 size={{ xs: 12 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block' }}
                                    >
                                        {t('shows.verification.helpInputLabel')}
                                    </Typography>
                                    <Box
                                        component="pre"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.78rem',
                                            whiteSpace: 'pre-wrap',
                                            textAlign: 'left',
                                            bgcolor: 'grey.100',
                                            borderRadius: 1,
                                            p: 1.5,
                                            mt: 0.5,
                                            mb: 0,
                                        }}
                                    >
                                        {INPUT_SHAPE_DOC + namesComment}
                                    </Box>
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 0.5,
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {t('shows.verification.scriptLabel')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title={t('shows.verification.formatScript')}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={formatScript}
                                                        disabled={!hasScript || isFormatting}
                                                    >
                                                        {isFormatting ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <FormatAlignLeftIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    copyAck
                                                        ? t('shows.verification.copyAck')
                                                        : t('shows.verification.copyScript')
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={copyScript}
                                                        disabled={!hasScript}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            bgcolor: 'grey.50',
                                            maxHeight: '60vh',
                                            overflow: 'auto',
                                            '&:focus-within': {
                                                borderColor: 'primary.main',
                                                boxShadow: (theme) =>
                                                    `0 0 0 1px ${theme.palette.primary.main}`,
                                            },
                                        }}
                                    >
                                        <CodeEditorErrorBoundary
                                            fallback={
                                                <TextField
                                                    multiline
                                                    fullWidth
                                                    minRows={12}
                                                    value={values.verificationScript || ''}
                                                    onChange={(ev) =>
                                                        onScriptChange(ev.target.value)
                                                    }
                                                    placeholder={t(
                                                        'shows.verification.scriptPlaceholder',
                                                    )}
                                                    slotProps={{
                                                        input: {
                                                            sx: {
                                                                fontFamily:
                                                                    'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                                fontSize: '0.85rem',
                                                            },
                                                        },
                                                    }}
                                                />
                                            }
                                        >
                                            <Editor
                                                value={values.verificationScript || ''}
                                                onValueChange={onScriptChange}
                                                highlight={(code) =>
                                                    hljs.highlight(code, { language: 'javascript' })
                                                        .value
                                                }
                                                padding={12}
                                                placeholder={t(
                                                    'shows.verification.scriptPlaceholder',
                                                )}
                                                style={{
                                                    fontFamily:
                                                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                    fontSize: '0.85rem',
                                                    minHeight: '240px',
                                                }}
                                            />
                                        </CodeEditorErrorBoundary>
                                    </Box>
                                </Grid2>

                                <Grid2 size={{ xs: 12 }} sx={{ mt: 1 }}>
                                    <Accordion disableGutters>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle2">
                                                {t('shows.verification.testRunTitle')}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid2 container spacing={2} size={{ xs: 12 }}>
                                                <Grid2 size={{ xs: 12, md: 7 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {t('shows.verification.testInputLabel')}
                                                        </Typography>
                                                        <Button
                                                            size="small"
                                                            onClick={resetTestInput}
                                                            sx={{ minWidth: 0 }}
                                                        >
                                                            {t('shows.verification.testReset')}
                                                        </Button>
                                                    </Box>
                                                    <TextField
                                                        value={testInput}
                                                        onChange={(e) =>
                                                            setTestInput(e.target.value)
                                                        }
                                                        multiline
                                                        minRows={12}
                                                        maxRows={20}
                                                        fullWidth
                                                        slotProps={{
                                                            input: {
                                                                sx: {
                                                                    fontFamily:
                                                                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                                    fontSize: '0.78rem',
                                                                },
                                                            },
                                                        }}
                                                    />
                                                    <Box sx={{ mt: 1 }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<PlayArrowIcon />}
                                                            onClick={runTest}
                                                            disabled={!hasScript}
                                                        >
                                                            {t('shows.verification.testRun')}
                                                        </Button>
                                                    </Box>
                                                </Grid2>
                                                <Grid2 size={{ xs: 12, md: 5 }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', mb: 0.5 }}
                                                    >
                                                        {t('shows.verification.testResultLabel')}
                                                    </Typography>
                                                    {!testOutput && (
                                                        <Box
                                                            sx={{
                                                                p: 1.5,
                                                                bgcolor: 'grey.50',
                                                                borderRadius: 1,
                                                                color: 'text.secondary',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {t('shows.verification.testIdle')}
                                                        </Box>
                                                    )}
                                                    {testOutput && testOutput.kind === 'empty' && (
                                                        <Box
                                                            sx={{
                                                                p: 1.5,
                                                                bgcolor: 'warning.light',
                                                                borderRadius: 1,
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {t('shows.verification.testEmpty')}
                                                        </Box>
                                                    )}
                                                    {testOutput &&
                                                        testOutput.kind === 'badInput' && (
                                                            <Box
                                                                sx={{
                                                                    p: 1.5,
                                                                    bgcolor: 'error.light',
                                                                    color: 'error.contrastText',
                                                                    borderRadius: 1,
                                                                    fontSize: '0.85rem',
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 0.5,
                                                                        mb: 0.5,
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    <ErrorOutlineIcon fontSize="small" />
                                                                    {t(
                                                                        'shows.verification.testBadInput',
                                                                    )}
                                                                </Box>
                                                                <Box
                                                                    component="pre"
                                                                    sx={{
                                                                        m: 0,
                                                                        whiteSpace: 'pre-wrap',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '0.78rem',
                                                                    }}
                                                                >
                                                                    {testOutput.message}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    {testOutput && testOutput.ok && (
                                                        <Box
                                                            sx={{
                                                                p: 1.5,
                                                                bgcolor: 'success.light',
                                                                color: 'success.contrastText',
                                                                borderRadius: 1,
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                    mb: 0.5,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                <CheckCircleOutlineIcon fontSize="small" />
                                                                {t(
                                                                    'shows.verification.testReturned',
                                                                )}
                                                            </Box>
                                                            <Box
                                                                component="pre"
                                                                sx={{
                                                                    m: 0,
                                                                    whiteSpace: 'pre-wrap',
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '0.85rem',
                                                                }}
                                                            >
                                                                {typeof testOutput.value ===
                                                                'undefined'
                                                                    ? 'undefined'
                                                                    : JSON.stringify(
                                                                          testOutput.value,
                                                                          null,
                                                                          2,
                                                                      )}
                                                            </Box>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ display: 'block', mt: 0.5 }}
                                                            >
                                                                {t(
                                                                    'shows.verification.testElapsed',
                                                                    {
                                                                        ms: testOutput.elapsed.toFixed(
                                                                            2,
                                                                        ),
                                                                    },
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {testOutput && testOutput.kind === 'thrown' && (
                                                        <Box
                                                            sx={{
                                                                p: 1.5,
                                                                bgcolor: 'error.light',
                                                                color: 'error.contrastText',
                                                                borderRadius: 1,
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                    mb: 0.5,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                <ErrorOutlineIcon fontSize="small" />
                                                                {testOutput.code
                                                                    ? t(
                                                                          'shows.verification.testThrownCode',
                                                                          { code: testOutput.code },
                                                                      )
                                                                    : t(
                                                                          'shows.verification.testThrown',
                                                                      )}
                                                            </Box>
                                                            {testOutput.mappedMessage && (
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ mb: 0.5 }}
                                                                >
                                                                    “{testOutput.mappedMessage}”
                                                                </Typography>
                                                            )}
                                                            <Box
                                                                component="pre"
                                                                sx={{
                                                                    m: 0,
                                                                    whiteSpace: 'pre-wrap',
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '0.78rem',
                                                                }}
                                                            >
                                                                {testOutput.message}
                                                                {testOutput.stack
                                                                    ? '\n\n' + testOutput.stack
                                                                    : ''}
                                                            </Box>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ display: 'block', mt: 0.5 }}
                                                            >
                                                                {t(
                                                                    'shows.verification.testElapsed',
                                                                    {
                                                                        ms: testOutput.elapsed.toFixed(
                                                                            2,
                                                                        ),
                                                                    },
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Grid2>
                                            </Grid2>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid2>

                                <Grid2 size={{ xs: 12 }} sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2">
                                        {t('shows.verification.messagesTitle')}
                                    </Typography>
                                </Grid2>

                                {rows.map((row, idx) => (
                                    <Grid2
                                        key={idx}
                                        size={{ xs: 12 }}
                                        container
                                        spacing={2}
                                        alignItems="center"
                                        sx={{ mb: 1 }}
                                    >
                                        <Grid2 size={{ xs: 12, md: 2 }}>
                                            <TextField
                                                label={t('shows.verification.codeLabel')}
                                                value={row.code}
                                                onChange={(e) =>
                                                    updateRow(idx, 'code', e.target.value)
                                                }
                                                type="number"
                                                fullWidth
                                                size="small"
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 9 }}>
                                            <TextField
                                                label={`${t(
                                                    'shows.verification.messageLabel',
                                                )} (${DEFAULT_LANG})`}
                                                value={row[DEFAULT_LANG] || ''}
                                                onChange={(e) =>
                                                    updateRow(idx, DEFAULT_LANG, e.target.value)
                                                }
                                                fullWidth
                                                size="small"
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Tooltip
                                                                title={t('common.translations')}
                                                            >
                                                                <span>
                                                                    <IconButton
                                                                        edge="end"
                                                                        size="small"
                                                                        disabled={
                                                                            !String(
                                                                                row.code || '',
                                                                            ).trim()
                                                                        }
                                                                        onClick={() =>
                                                                            openTranslations(idx)
                                                                        }
                                                                    >
                                                                        <LanguageIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 1 }}>
                                            <IconButton
                                                aria-label={t('common.delete')}
                                                onClick={() => removeRow(idx)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid2>
                                    </Grid2>
                                ))}

                                <Grid2 size={{ xs: 12 }} sx={{ mt: 1 }}>
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={addRow}
                                        variant="outlined"
                                        size="small"
                                    >
                                        {t('shows.verification.addMessage')}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </AccordionDetails>
                    </Accordion>
                </Grid2>
            </>
        );
    },
);

ShowVerificationSection.displayName = 'ShowVerificationSection';

export default ShowVerificationSection;
